/**
 * Companion bonding — the prompt catalog, pacing budget, and the pure selector
 * (`pickNextPrompt`) that decides which single question, if any, the companion should
 * ask the player right now.
 *
 * Pure data + selection. No RNG, no I/O, no `process.env`, no Date.now() — callers pass
 * `nowMs` explicitly so this module stays deterministic and testable.
 *
 * Three prompt categories, asked in this priority order whenever more than one is due:
 *   1. personal  — get-to-know-you questions (nickname, favorite food, favorite color...)
 *   2. feedback  — asked only once the player has actually done the relevant thing
 *                  (battled, started the campaign...)
 *   3. community — asked only once the bond is old enough, nudging real-world actions
 *                  (told a friend, follows on X, joined Telegram)
 *
 * The nickname prompt (`player_nickname`) is always `order 0` inside `personal` and is
 * asked first — it is ALWAYS eligible (never skipped) until answered, even when the
 * player already has a display name (e.g. from social login). When a display name
 * exists, the caller (companionService) swaps the open `input:'text'` prompt for an
 * `input:'nickname_confirm'` variant using `confirmPrompts` below, so the companion asks
 * "I've been calling you {player} — that right?" instead of a bare open question.
 * Whichever variant is shown, answering writes a `player_nickname` response row so the
 * prompt is asked at most once.
 */

import type { PersonalityId } from './companionMessages.js';
import { DISPLAY_NAME_MAX_LENGTH } from './displayNameValidation.js';

export type PromptCategory = 'personal' | 'feedback' | 'community';

export interface PromptChoice {
  id: string;
  label: string;
}

export interface PromptEligibility {
  hasBattled: boolean;
  campaignStarted: boolean;
  bondAgeMs: number;
  hasDisplayName: boolean;
}

export interface PromptDef {
  key: string;
  category: PromptCategory;
  /** Ordering within the category — lower asked first. */
  order: number;
  input: 'text' | { choices: PromptChoice[] };
  prompts: Record<PersonalityId, string>;
  /**
   * `player_nickname` only — per-personality confirm text ("I've been calling you {player}
   * — that right?") shown instead of `prompts` when the player already has a display name.
   */
  confirmPrompts?: Record<PersonalityId, string>;
  /** Gate for feedback/community prompts — personal prompts (besides nickname) have none. */
  eligible?: (ctx: PromptEligibility) => boolean;
  /** Community prompts only — which CTA URL to surface alongside the prompt. */
  ctaUrlKey?: 'x' | 'telegram' | 'referral';
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Minimum gap between two prompts of any kind, regardless of category. */
export const PROMPT_MIN_INTERVAL_MS = 3 * ONE_DAY_MS;

/** Minimum bond age before "interview" (personal) prompts other than nickname are asked. */
export const INTERVIEW_MIN_BOND_AGE_MS = 3 * ONE_DAY_MS;

/** Minimum bond age before community prompts are asked. */
export const COMMUNITY_MIN_BOND_AGE_MS = 7 * ONE_DAY_MS;

/** Re-exported so callers building the nickname UI don't need a second import. */
export const NICKNAME_MAX_LENGTH = DISPLAY_NAME_MAX_LENGTH;

/** Sentinel value the frontend sends for `nickname_confirm` when the player keeps their current name. */
export const NICKNAME_CONFIRM_VALUE = '__keep_current_name__';

/**
 * Maps the `CompanionMessageContext` fact tokens (`favFood`/`favColor`/`goal`) to the
 * `prompt_key` each is stored under in `CompanionBondPayload.facts` (see the `personal`
 * prompts above). Single source of truth so the fact-token → stored-key mapping lives in
 * one place instead of being duplicated (and drifting) at each call site.
 */
export const COMPANION_FACT_PROMPT_KEYS = {
  favFood: 'favorite_food',
  favColor: 'favorite_color',
  goal: 'player_goal',
} as const satisfies Record<'favFood' | 'favColor' | 'goal', string>;

const CATEGORY_PRIORITY: Record<PromptCategory, number> = {
  personal: 0,
  feedback: 1,
  community: 2,
};

export const PROMPTS: readonly PromptDef[] = [
  // ---- personal ------------------------------------------------------------------
  {
    key: 'player_nickname',
    category: 'personal',
    order: 0,
    input: 'text',
    prompts: {
      sunny: "Hey, what should I call you? I want a name just for us! 🌞",
      sage: 'What name would you have me use for you?',
      rascal: "Psst — what's your name? I promise not to make fun of it. Much.",
      champion: "What do I call you, trainer? Every good team needs proper names.",
    },
    confirmPrompts: {
      sunny: "So I've been calling you {player} — is that right? 😊",
      sage: 'I have addressed you as {player}. Is that the name you prefer?',
      rascal: "I've been calling you {player}. That still checking out, or nah?",
      champion: "I've been calling you {player}, trainer. Correct, or do we change it?",
    },
  },
  {
    key: 'favorite_food',
    category: 'personal',
    order: 1,
    input: 'text',
    prompts: {
      sunny: "Ooh, quick question — what's your favorite food? I want to know everything about you! 💛",
      sage: 'A small question: what food brings you the most comfort?',
      rascal: "Tell me your favorite food. I'm not planning anything, I swear.",
      champion: "What's your go-to meal before a big day? A trainer needs fuel.",
    },
  },
  {
    key: 'favorite_color',
    category: 'personal',
    order: 2,
    input: 'text',
    prompts: {
      sunny: "What's your favorite color? Mine changes depending on how happy I am! 🌈",
      sage: 'If you had to choose one color to represent you, which would it be?',
      rascal: "Favorite color, go! No overthinking it.",
      champion: "What color's your banner? Every champion needs one.",
    },
  },
  {
    key: 'why_suikamon',
    category: 'personal',
    order: 3,
    input: 'text',
    prompts: {
      sunny: "Can I ask... why did you pick me? I think about it a lot! 🥹",
      sage: 'May I ask what drew you to adopt a Suikamon?',
      rascal: "Real talk — why'd you adopt a Suikamon? Boredom? Fate? Peer pressure?",
      champion: "What made you decide to raise a Suikamon? I want to know what I'm fighting for.",
    },
  },
  {
    key: 'player_goal',
    category: 'personal',
    order: 4,
    input: 'text',
    prompts: {
      sunny: "What's your big dream for us? I'll cheer you on no matter what! 🌟",
      sage: 'What do you hope we achieve together, in time?',
      rascal: "What's the plan, boss? World domination? Just vibes? Tell me.",
      champion: "What's our goal here — climb the ranks? Fill the collection? Say the word.",
    },
  },
  // ---- feedback --------------------------------------------------------------------
  {
    key: 'feedback_care',
    category: 'feedback',
    order: 0,
    input: { choices: [{ id: 'good', label: 'Feels great' }, { id: 'confusing', label: 'A bit confusing' }, { id: 'tedious', label: 'Kind of tedious' }] },
    prompts: {
      sunny: "How's taking care of me feeling so far — good, confusing, or a little tedious? Be honest! 💛",
      sage: 'How does the daily care of me feel to you — good, confusing, or tedious?',
      rascal: "Real talk: is looking after me fun, confusing, or a chore? I can take it.",
      champion: "Straight answer, trainer — is the daily care good, confusing, or tedious?",
    },
    eligible: (ctx) => ctx.bondAgeMs >= INTERVIEW_MIN_BOND_AGE_MS,
  },
  {
    key: 'feedback_battles',
    category: 'feedback',
    order: 1,
    input: { choices: [{ id: 'good', label: 'Feels great' }, { id: 'confusing', label: 'A bit confusing' }, { id: 'tedious', label: 'Kind of tedious' }] },
    prompts: {
      sunny: "How did our battle feel — exciting, confusing, or a bit much? I want us to have fun out there! 🌞",
      sage: 'How did the battle feel to you — good, confusing, or tedious?',
      rascal: "So — was that battle a blast, a headache, or a snooze? Spill it.",
      champion: "Give it to me straight — was that fight good, confusing, or tedious?",
    },
    eligible: (ctx) => ctx.hasBattled,
  },
  {
    key: 'feedback_campaign',
    category: 'feedback',
    order: 2,
    input: { choices: [{ id: 'good', label: 'Feels great' }, { id: 'confusing', label: 'A bit confusing' }, { id: 'tedious', label: 'Kind of tedious' }] },
    prompts: {
      sunny: "How's our campaign journey feeling — good, confusing, or a little tedious? I love our adventures! 🌟",
      sage: 'How has the campaign felt so far — good, confusing, or tedious?',
      rascal: "Level with me — is the campaign fun, confusing, or dragging on?",
      champion: "How's the campaign treating you — good, confusing, or tedious? No sugarcoating.",
    },
    eligible: (ctx) => ctx.campaignStarted,
  },
  // ---- community -------------------------------------------------------------------
  {
    key: 'told_friend',
    category: 'community',
    order: 0,
    input: { choices: [{ id: 'yes', label: 'Yes' }, { id: 'not_yet', label: 'Not yet' }] },
    prompts: {
      sunny: "Have you told a friend about us yet? I'd love to meet more people! 💛",
      sage: 'Have you shared word of us with a friend yet?',
      rascal: "Have you bragged about me to a friend yet? You should.",
      champion: "Have you told a friend about our progress yet? Word should spread.",
    },
    eligible: (ctx) => ctx.bondAgeMs >= COMMUNITY_MIN_BOND_AGE_MS,
    ctaUrlKey: 'referral',
  },
  {
    key: 'follows_x',
    category: 'community',
    order: 1,
    input: { choices: [{ id: 'yes', label: 'Yes' }, { id: 'not_yet', label: 'Not yet' }] },
    prompts: {
      sunny: "Do you follow us on X yet? I post the cutest updates! 🌞",
      sage: 'Do you follow our page on X?',
      rascal: "Following us on X yet? It's where I roast the other Suikamon.",
      champion: "Are you following us on X? Stay sharp on the latest news.",
    },
    eligible: (ctx) => ctx.bondAgeMs >= COMMUNITY_MIN_BOND_AGE_MS,
    ctaUrlKey: 'x',
  },
  {
    key: 'joined_telegram',
    category: 'community',
    order: 2,
    input: { choices: [{ id: 'yes', label: 'Yes' }, { id: 'not_yet', label: 'Not yet' }] },
    prompts: {
      sunny: "Have you joined our Telegram yet? It's full of friendly faces! 💛",
      sage: 'Have you joined our Telegram community?',
      rascal: "Joined the Telegram yet? That's where the real chaos happens.",
      champion: "Have you joined our Telegram? It's where the team rallies.",
    },
    eligible: (ctx) => ctx.bondAgeMs >= COMMUNITY_MIN_BOND_AGE_MS,
    ctaUrlKey: 'telegram',
  },
];

interface PickNextPromptArgs {
  answeredKeys: string[];
  lastPromptedMs: number;
  nowMs: number;
  eligibility: PromptEligibility;
  personality: PersonalityId;
}

/**
 * Prompts that are defined but never asked. The definition (and any stored answer) stays so
 * re-enabling is deleting the key here.
 */
export const DISABLED_PROMPT_KEYS: ReadonlySet<string> = new Set(['player_nickname']);

function isPromptEligible(def: PromptDef, eligibility: PromptEligibility): boolean {
  if (DISABLED_PROMPT_KEYS.has(def.key)) {
    return false;
  }
  if (def.key === 'player_nickname') {
    return true;
  }
  if (def.category === 'personal') {
    return eligibility.bondAgeMs >= INTERVIEW_MIN_BOND_AGE_MS;
  }
  // feedback / community always carry their own eligible() predicate.
  return def.eligible ? def.eligible(eligibility) : true;
}

export function pickNextPrompt(args: PickNextPromptArgs): { def: PromptDef; text: string } | null {
  const { answeredKeys, lastPromptedMs, nowMs, eligibility, personality } = args;

  if (nowMs - lastPromptedMs < PROMPT_MIN_INTERVAL_MS) {
    return null;
  }

  const answered = new Set(answeredKeys);

  const candidates = PROMPTS.filter((def) => !answered.has(def.key) && isPromptEligible(def, eligibility));

  if (candidates.length === 0) {
    return null;
  }

  candidates.sort((a, b) => {
    const catDiff = CATEGORY_PRIORITY[a.category] - CATEGORY_PRIORITY[b.category];
    if (catDiff !== 0) return catDiff;
    return a.order - b.order;
  });

  const def = candidates[0];
  const text = def.prompts[personality];
  return { def, text };
}
